import { ReactNode } from 'react';
import { t } from '@lingui/macro';
import { getDurationUnit } from 'utils/durationHelper';
import { Offer, TColumnData } from '../SummaryCard.helpers';
import { TInitialCommission } from './InitialCommission/InitialCommission';

type TGetClipboardTextParams = {
  isUK: boolean;
  withdrawalFee: number;
  offer: TColumnData;
  formattedMonthlyRepayments: string | null;
  formatCurrency: any;
  formatPercentage: any;
  initialCommission?: TInitialCommission;
};

type TOfferSelectedStatus = {
  offerIsSelected: boolean;
  offerNotSelected: boolean;
};

export type TValueProps = {
  offerSelection: TOfferSelectedStatus;
  children?: ReactNode;
  className?: string;
  value?: string;
} & ({ children: ReactNode } | { value: string });

export function getOfferSelectionStatus(
  offerDecision: Offer,
  selectedOfferId: string,
  offerId?: string,
): TOfferSelectedStatus {
  const offerIsSelected =
    selectedOfferId === offerId && offerDecision !== Offer.REJECT;

  const offerNotSelected = Boolean(
    (selectedOfferId && !offerIsSelected && offerDecision === Offer.AGREE) ||
      offerDecision === Offer.REJECT,
  );

  return {
    offerIsSelected,
    offerNotSelected,
  };
}

export function hasRequiredWithdrawalFeeData({
  offer_id,
  withdrawal_fee_percentage,
}: TColumnData) {
  return Boolean(
    offer_id && (withdrawal_fee_percentage || withdrawal_fee_percentage === 0),
  );
}

/**
 * Generate clipboard text
 */
export function getClipboardText({
  isUK,
  offer,
  withdrawalFee,
  formattedMonthlyRepayments,
  formatCurrency,
  formatPercentage,
  initialCommission,
}: TGetClipboardTextParams) {
  const { duration, interest_rates, max_amount } = offer;

  const interestRate = interest_rates?.[0]?.rate;

  const durationValue = duration
    ? `${duration.amount} ${getDurationUnit(duration.unit)}`
    : '';

  const initialCommissionCopy = initialCommission
    ? `\nInitial commission: ${formatPercentage(
        initialCommission.rate,
      )} (${formatCurrency(initialCommission.amount)})`
    : '';

  const repeatCommissionCopy = initialCommission
    ? `\nRepeat commission: ${formatPercentage(initialCommission.repeat_rate)}`
    : '';

  const PGCopy =
    offer.personal_guarantee_proportion === 0 && isUK
      ? `\nPersonal guarantor: Not required`
      : '';

  const textToCopy = `${t`Term length`}: ${durationValue}\
        \n${t`Offer amount`}: ${formatCurrency(max_amount)}\
        \n${t`30 day interest rate`}: ${formatPercentage(interestRate)}\
        \n${t`Withdrawal fee`}: ${formatPercentage(withdrawalFee)}\
        ${initialCommissionCopy}\
        ${repeatCommissionCopy}\
        \n${t`Monthly repayments`}: ${formattedMonthlyRepayments}\
        ${PGCopy}`;

  return textToCopy;
}
